import React, {
    useContext, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { tp } from 'app/utils/typograf';

import { isStandaloneApp } from 'app/utils/browser';
import { DishAdLabel } from 'app/components/ui/Elements/DishAdLable/DishAdLabel';

import { DishImageShield } from 'app/components/ui/Elements/DishImageShield/DishImageShield';
import { Desktop, Mobile } from 'app/components/Responsive';
import DishPortionCounter from '../../DishButtons/DishPortionCounter';
import DishButton, { OrderButton } from '../../DishButtons/DishButton';

import { DishMediaContent } from '../DishMediaContent';
import { DishTitle } from '../DishTitile';

import { NYGiftContext } from '../../NYGiftContext';
import { ConfigContext } from '../../ConfigProvider';
import { DishButtonRounded } from '../../DishButtons/DIshButtonRounded';

export function DishLayoutThin({
    metaLabel,
    index,
    id,
    parentId,
    isSoldOut,
    isWeightTimeHidden,
    isTimeHidden,
    hideWeightOnDesktop,
    partnersComment,
    portion,
    maxPortion,
    basketDishes,
    dishesById,
    basketDishesById,
    sortedDishes,
    originalPriceWithStep,
    discountPriceWithStep,
    price,
    dishCountStep,
    imageUrl,
    imageUrlDesktop,
    verticalImage,
    time,
    hour,
    minutes,
    weightValue,
    weight,
    isCentered,
    buttonsDisabled,
    canChangePortionsCount,
    areDetailsEnabled: areDetailsEnabledProp,
    buttonText,
    buttonTextAction,
    linkUrl,
    imageLinkUrl,
    typeformWidgetId,
    isNovelty,
    colors,
    nutrition,
    isNutritionEnabled,
    isLastDish,
    slides,
    joinDishTitlesinMobile,
    title,
    titleForSmallCards,
    description,
    hourText,
    minutesText,
    weightText,
    timeText,
    trackAddToBasket,
    v2Layout,
    giftWrapId = undefined,
    pageId,
    tags,
}) {
    const isUpcoming = Boolean(typeformWidgetId);
    const hasLabel = isSoldOut || isUpcoming || isNovelty;

    const config = useContext(ConfigContext);
    const isCustomizationEnabled = useMemo(
        () => config?.isCustomizationEnabled || false,
        [config],
    );

    const [selectedDishId, setSelectedDishId] = useState(id);

    const areDetailsEnabled = id === 'ym' ? false : areDetailsEnabledProp;

    const rootClasses = cn({
        ny__dish__container: true,
        'ny__dish__container-v2': v2Layout,
        'ny__dish__container-thin': true,
        'ny__dish__container--last': isLastDish,
        'ny__dish__container--has-label': hasLabel,
        'ny__dish__container--has-label-sold-out': isSoldOut,
        'ny__dish__container--has-label-upcoming': !isSoldOut && isUpcoming,
        'ny__dish__container--has-label-novelty': !isSoldOut && isNovelty,
        'ny__dish__container--without-details': !areDetailsEnabled,
        thin: true,
        [pageId]: true,
    });

    const childDish = useMemo(() => {
        if (selectedDishId === id) {
            return null;
        }

        if (dishesById) {
            return dishesById[selectedDishId];
        }

        return sortedDishes?.find((d) => d.id === selectedDishId) || null;
    }, [dishesById, sortedDishes, id, selectedDishId]);

    const basketChildDish = useMemo(() => {
        if (!childDish) {
            return undefined;
        }

        if (basketDishesById) {
            if (childDish.isSet) {
                return childDish.dishes
                    ?.map((childId) => basketDishesById[childId])
                    .find(Boolean);
            }

            return basketDishesById[childDish.id];
        }

        return basketDishes?.find((bd) => {
            if (childDish?.isSet) {
                return childDish?.dishes.includes(bd.id);
            }
            return bd.id === childDish?.id;
        });
    }, [basketDishes, basketDishesById, childDish]);

    const cardSlides = slides.filter((s) => s.placement === 'card');
    const containerProps = {
        'data-id': id,
        id: `dish_${id}`,
    };

    const textContainerProps = {};

    if (cardSlides.length === 0) {
        containerProps['data-toggle-details-id'] = selectedDishId;
    } else {
        textContainerProps['data-toggle-details-id'] = selectedDishId;
    }

    /* FRNT-3400 NY2024 ПОДАРОЧНЫЙ КОСТЫЛЬ */
    const {
        isGiftItem,
        getItemsListById,
    } = useContext(NYGiftContext);

    const portionWithGift = useMemo(() => {
        if (isGiftItem(id)) {
            const { itemsList } = getItemsListById(id);
            return itemsList.length;
        }
        if (!childDish) return portion;

        return basketChildDish?.portion || 0;
    }, [portion, isGiftItem, id, getItemsListById, childDish, basketChildDish]);

    const tagsString = tags?.map((tag) => tag.id)?.join(',');

    const currentDishParams = useMemo(
        () => {
            if (childDish) {
                return {
                    ...childDish,
                    title: childDish?.title,
                    joinDishTitlesinMobile: childDish?.joinDishTitlesinMobile,
                    description: childDish?.description,
                    price: childDish?.price,
                    imageUrl: childDish?.imageUrl,
                    imageUrlDesktop: childDish?.verticalImage || childDish?.imageUrlDesktop,
                    titleForSmallCards: childDish?.titleForSmallCards,
                };
            }
            return {
                title,
                titleForSmallCards,
                joinDishTitlesinMobile,
                description,
                price,
                imageUrl,
                imageUrlDesktop: verticalImage || imageUrlDesktop,
            };
        },
        [
            childDish,
            title,
            joinDishTitlesinMobile,
            description,
            price,
            imageUrl,
            imageUrlDesktop,
            verticalImage,
        ],
    );

    return (
        <div
            {...containerProps}
            className={rootClasses}
            role="none"
        >
            <Mobile {...textContainerProps}>
                <div className="ny__dish__image-container-thin">
                    <img
                        className="ny__dish__image-thin"
                        src={currentDishParams.imageUrl}
                        alt={title[0]}
                    />
                    {canChangePortionsCount && !isSoldOut && !isUpcoming && (
                        <DishButtonRounded
                            id={selectedDishId}
                            giftWrapId={giftWrapId}
                            originalPriceWithStep={isCustomizationEnabled ? currentDishParams?.price : originalPriceWithStep}
                            discountPriceWithStep={isCustomizationEnabled ? currentDishParams?.price : discountPriceWithStep}
                            price={isCustomizationEnabled ? currentDishParams?.price : price}
                            dishCountStep={dishCountStep}
                            portion={basketChildDish ? basketChildDish?.portion : portion}
                            maxPortion={maxPortion}
                            buttonsDisabled={buttonsDisabled}
                            onClickAddToBasket={trackAddToBasket}
                            thin
                        />
                    )}
                </div>
                <div className="ny__dish__content-thin">
                    <span className="ny__dish__price-thin">{price}</span>
                    <span className="ny__dish__price-ruble-thin">₽</span>
                    <span className="ny__dish__weight thin">·</span>
                    <div className="ny__dish__weight_cont">
                        <span className="ny__dish__weight thin">{weight}</span>
                        <span className="ny__dish__weight thin unit">{weightText}</span>
                    </div>
                </div>
                {titleForSmallCards
                    ? (
                        titleForSmallCards?.map((t) => (
                            <div className="ny__dish__title">{tp.execute(t)}</div>
                        ))
                    )
                    : <div className="ny__dish__title">{tp.execute(title[0])}</div>}
            </Mobile>
            <Desktop>
                <DishMediaContent
                    id={selectedDishId}
                    useLazyLoading={index === 0}
                    alt={title[0]}
                    imageUrl={currentDishParams.imageUrl}
                    imageUrlDesktop={currentDishParams?.imageUrlDesktop}
                    slides={cardSlides}
                    additionalContent={(
                        <>
                            <DishAdLabel id={selectedDishId} />
                            <DishImageShield
                                dishId={selectedDishId}
                                isSoldOut={isSoldOut}
                                tagsString={tagsString}
                                realm="lp"
                            />
                            {canChangePortionsCount && <DishPortionCounter portion={portionWithGift} />}
                            {imageLinkUrl && (
                                <a
                                    className="ny__dish__link-opener"
                                    href={imageLinkUrl}
                                    target={isStandaloneApp() ? '_self' : '_blank'}
                                    rel="noopener noreferrer"
                                >
                                    {imageLinkUrl}
                                </a>
                            )}
                        </>
                    )}
                />

                <div className="ny__dish__bottom-container thin" {...textContainerProps}>
                    <div>
                        <div className="ny__dish__desc-container thin">
                            <DishTitle
                                joinDishTitlesinMobile={currentDishParams?.joinDishTitlesinMobile}
                                title={currentDishParams?.title}
                                colors={colors}
                                layout="thin"
                            />
                        </div>
                    </div>
                    {!hideWeightOnDesktop && (((weight || time) && !isWeightTimeHidden && !isUpcoming) ? (
                        <div>
                            {/* КОСТЫЛЬ ДЛЯ КОРРЕКТНОГО INLINE CONTEXT */}
                            <div style={{ fontSize: 0 }}>
                                {weight ? (
                                    <span className="ny__dish__weight thin">
                                        <span className="num">{weight}</span>
                                        &nbsp;
                                        {weightText}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    ) : (
                        <div className="ny__dish__info-container ny__dish__info-container--stub">&nbsp;</div>
                    ))}

                    <div
                        style={{ marginTop: 'auto' }}
                        class={isCustomizationEnabled ? 'ny__dish__bottom-customization-container' : null}
                    >
                        <div data-dish-button>
                            {(buttonText) ? (
                                <OrderButton
                                    text={buttonText}
                                    linkUrl={linkUrl}
                                    isOpenDetailsHandler={isUpcoming}
                                    withCustomizationSelect={false}
                                />
                            ) : (
                                canChangePortionsCount && !isSoldOut && !isUpcoming && (
                                    <DishButton
                                        id={selectedDishId}
                                        giftWrapId={giftWrapId}
                                        originalPriceWithStep={isCustomizationEnabled ? currentDishParams?.price : originalPriceWithStep}
                                        discountPriceWithStep={isCustomizationEnabled ? currentDishParams?.price : discountPriceWithStep}
                                        price={isCustomizationEnabled ? currentDishParams?.price : price}
                                        dishCountStep={dishCountStep}
                                        portion={basketChildDish ? basketChildDish?.portion : portion}
                                        maxPortion={maxPortion}
                                        isInfoBlock
                                        buttonsDisabled={buttonsDisabled}
                                        onClickAddToBasket={trackAddToBasket}
                                        withCustomizationSelect={false}
                                        thin
                                    />
                                )
                            )}
                        </div>
                    </div>
                </div>
            </Desktop>
        </div>
    );
}

DishLayoutThin.propTypes = {
    id: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    giftWrapId: PropTypes.string,
    hideWeightOnDesktop: PropTypes.bool,
};
